/**
 * AxLabs Biology Experiments - Cell Transport
 *
 * Collection of cell transport experiments generated using
 * the declarative framework.
 */
import { createExperimentFromSchema, IDEAL_GAS_CONSTANT, STANDARD_TEMPERATURE } from '../../framework/index.js';
import { getParam, getState, getStateValues } from '../../physics/generated/helpers.js';
// Conversion factor from L·atm/(mol·K) to appropriate units
const R = IDEAL_GAS_CONSTANT;
// ============================================================================
// FACILITATED DIFFUSION (SATURATION KINETICS)
// ============================================================================
export const FacilitatedDiffusion = createExperimentFromSchema({
    id: 'facilitated-diffusion',
    name: 'Facilitated Diffusion (Saturation Kinetics)',
    domain: 'biology',
    description: 'Carrier-mediated transport following Michaelis-Menten kinetics',
    conceptTags: ['Facilitated Diffusion', 'Michaelis-Menten', 'Carrier Proteins', 'Saturation Kinetics'],
    difficulty: 5,
    stateVariables: [
        { id: 'C_out', name: 'Extracellular Concentration', unit: 'mM', initial: 10, symbol: 'C_{out}', description: 'Solute concentration outside cell' },
    ],
    parameters: [
        { id: 'V_max', name: 'Maximum Transport Rate', unit: 'mmol/s', min: 0.1, max: 100, default: 10, symbol: 'V_{max}', description: 'Maximum rate when carriers are saturated' },
        { id: 'K_m', name: 'Michaelis Constant', unit: 'mM', min: 0.1, max: 50, default: 5, symbol: 'K_m', description: 'Concentration at half V_max' },
        { id: 'C_in_initial', name: 'Initial Intracellular Concentration', unit: 'mM', min: 0, max: 10, default: 1, symbol: 'C_{in}(0)' },
        { id: 'cell_volume', name: 'Cell Volume', unit: 'L', min: 1e-12, max: 1e-9, default: 1e-10, symbol: 'V_{cell}' },
    ],
    equations: [
        {
            id: 'michaelis-menten',
            name: 'Michaelis-Menten Equation',
            latex: 'v = \\frac{V_{max} \\cdot C_{out}}{K_m + C_{out}}',
            description: 'Transport rate saturates at high concentrations',
        },
        {
            id: 'flux',
            name: 'Flux into Cell',
            latex: 'J = \\frac{V_{max}}{V_{cell}} \\cdot \\frac{C_{out}}{K_m + C_{out}}',
            description: 'Rate of concentration change inside cell',
        },
    ],
    derivatives: (_state, _params) => ({
        C_out: 0,
    }),
    derivedState: (state, params) => {
        const C_out = getState(state, 'C_out');
        const V_max = getParam(params, 'V_max');
        const K_m = getParam(params, 'K_m');
        const cell_volume = getParam(params, 'cell_volume');
        const transport_rate = V_max * C_out / (K_m + C_out);
        const flux = transport_rate / cell_volume;
        const saturation = C_out / (K_m + C_out);
        const at_half_max = Math.abs(C_out - K_m) < 0.1 ? 1 : 0;
        return {
            transport_rate,
            flux,
            net_flux: flux,
            saturation_fraction: saturation,
            at_half_max,
            percent_max_rate: saturation * 100,
        };
    },
    failureModes: [
        {
            id: 'negative-concentration',
            name: 'Negative Concentration',
            check: (_state, _params) => {
                const C_out = getState(getStateValues(_state), 'C_out');
                return C_out < 0;
            },
            severity: 'error',
            educationalMessage: 'Concentration cannot be negative.',
        },
    ],
    bounds: {
        C_out: { min: 0, max: 200 },
    },
});
// ============================================================================
// ACTIVE TRANSPORT (ATP-COUPLED)
// ============================================================================
export const ActiveTransportATP = createExperimentFromSchema({
    id: 'active-transport-atp',
    name: 'Active Transport (ATP-Coupled)',
    domain: 'biology',
    description: 'ATP-driven transport against concentration gradient',
    conceptTags: ['Active Transport', 'ATP Hydrolysis', 'Primary Active Transport', 'P-type ATPases'],
    difficulty: 6,
    stateVariables: [
        { id: 'C_in', name: 'Intracellular Concentration', unit: 'mM', initial: 5, symbol: 'C_{in}', description: 'Ion concentration inside cell' },
        { id: 'ATP', name: 'ATP Concentration', unit: 'mM', initial: 5, symbol: '[ATP]', description: 'Cellular ATP concentration' },
    ],
    parameters: [
        { id: 'C_out', name: 'Extracellular Concentration', unit: 'mM', min: 1, max: 200, default: 145, symbol: 'C_{out}', description: 'External ion concentration (e.g., Na+)' },
        { id: 'V_max', name: 'Maximum Pump Rate', unit: 'mmol/s', min: 0.01, max: 50, default: 5, symbol: 'V_{max}' },
        { id: 'K_m_ATP', name: 'K_m for ATP', unit: 'mM', min: 0.01, max: 5, default: 0.5, symbol: 'K_{m,ATP}' },
        { id: 'n_ions', name: 'Ions per ATP', unit: '', min: 1, max: 4, default: 3, symbol: 'n', description: 'Ions transported per ATP hydrolyzed' },
        { id: 'K_d_ion', name: 'Ion Dissociation Constant', unit: 'mM', min: 0.1, max: 50, default: 10, symbol: 'K_d' },
        { id: 'cell_volume', name: 'Cell Volume', unit: 'L', min: 1e-12, max: 1e-9, default: 1e-10, symbol: 'V_{cell}' },
    ],
    equations: [
        {
            id: 'atp-dependence',
            name: 'ATP Dependence',
            latex: 'v = V_{max} \\cdot \\frac{[ATP]}{K_{m,ATP} + [ATP]}',
            description: 'Rate depends on ATP availability',
        },
        {
            id: 'stoichiometry',
            name: 'Transport Stoichiometry',
            latex: 'n \\cdot Ion + ATP \\rightarrow n \\cdot Ion_{in} + ADP + P_i',
            description: 'n ions transported per ATP hydrolyzed',
        },
        {
            id: 'free-energy',
            name: 'Free Energy Requirement',
            latex: '\\Delta G = nRT\\ln\\frac{C_{in}}{C_{out}} + \\Delta G_{ATP}',
            description: 'Energy cost to move ions against gradient',
        },
    ],
    derivatives: (_state, _params) => ({
        C_in: 0,
        ATP: 0,
    }),
    derivedState: (state, params) => {
        const C_in = getState(state, 'C_in');
        const ATP = getState(state, 'ATP');
        const C_out = getParam(params, 'C_out');
        const V_max = getParam(params, 'V_max');
        const K_m_ATP = getParam(params, 'K_m_ATP');
        const n_ions = getParam(params, 'n_ions');
        const K_d = getParam(params, 'K_d_ion');
        const cell_volume = getParam(params, 'cell_volume');
        const T = STANDARD_TEMPERATURE;
        // Pump rate depends on ATP concentration
        const ATP_saturation = ATP / (K_m_ATP + ATP);
        const ion_saturation = C_out / (K_d + C_out);
        const pump_rate = V_max * ATP_saturation * ion_saturation;
        const flux_in = pump_rate / cell_volume;
        // Free energy calculations
        const Delta_G_ion = n_ions * R * T * Math.log(C_in / C_out); // J/mol
        const Delta_G_ATP = -50000; // Approx -50 kJ/mol for ATP hydrolysis
        const Delta_G_total = Delta_G_ion + Delta_G_ATP;
        return {
            pump_rate,
            flux_in,
            ATP_saturation,
            ion_saturation,
            concentration_gradient: C_out - C_in,
            Delta_G_ion_kJ: Delta_G_ion / 1000,
            Delta_G_total_kJ: Delta_G_total / 1000,
            energetically_favorable: Delta_G_total < 0 ? 1 : 0,
            ATP_required_per_ion: n_ions,
        };
    },
    failureModes: [
        {
            id: 'negative-concentration',
            name: 'Negative Concentration',
            check: (_state, _params) => {
                const C_in = getState(getStateValues(_state), 'C_in');
                const ATP = getState(getStateValues(_state), 'ATP');
                return C_in < 0 || ATP < 0;
            },
            severity: 'error',
            educationalMessage: 'Concentrations cannot be negative.',
        },
        {
            id: 'atp-depletion',
            name: 'ATP Depletion',
            check: (_state, params) => {
                const ATP = getState(getStateValues(_state), 'ATP');
                const K_m_ATP = getParam(params, 'K_m_ATP');
                return ATP < K_m_ATP / 10;
            },
            severity: 'warning',
            educationalMessage: 'ATP is severely depleted. The pump will operate at less than 10% of maximum rate.',
        },
    ],
    bounds: {
        C_in: { min: 0, max: 200 },
        ATP: { min: 0, max: 10 },
    },
});
// ============================================================================
// NA+/K+ ATPASE PUMP
// ============================================================================
export const SodiumPotassiumATPase = createExperimentFromSchema({
    id: 'sodium-potassium-atpase',
    name: 'Na+/K+ ATPase Pump',
    domain: 'biology',
    description: 'Sodium-potassium pump maintaining membrane potential',
    conceptTags: ['Na+/K+ ATPase', 'Membrane Potential', 'Electrogenic Pump', 'Resting Potential'],
    difficulty: 7,
    stateVariables: [
        { id: 'Na_in', name: 'Intracellular Na+', unit: 'mM', initial: 15, symbol: '[Na^+]_{in}', description: 'Sodium concentration inside cell' },
        { id: 'K_in', name: 'Intracellular K+', unit: 'mM', initial: 140, symbol: '[K^+]_{in}', description: 'Potassium concentration inside cell' },
        { id: 'V_m', name: 'Membrane Potential', unit: 'mV', initial: -70, symbol: 'V_m', description: 'Resting membrane potential' },
    ],
    parameters: [
        { id: 'Na_out', name: 'Extracellular Na+', unit: 'mM', min: 100, max: 200, default: 145, symbol: '[Na^+]_{out}' },
        { id: 'K_out', name: 'Extracellular K+', unit: 'mM', min: 2, max: 10, default: 5, symbol: '[K^+]_{out}' },
        { id: 'V_max', name: 'Maximum Pump Rate', unit: 'mmol/s', min: 0.1, max: 20, default: 2, symbol: 'V_{max}' },
        { id: 'cell_volume', name: 'Cell Volume', unit: 'L', min: 1e-12, max: 1e-9, default: 1e-10, symbol: 'V_{cell}' },
        { id: 'T', name: 'Temperature', unit: 'K', min: 290, max: 315, default: 310.15, symbol: 'T' },
    ],
    equations: [
        {
            id: 'pump-stoichiometry',
            name: 'Pump Stoichiometry',
            latex: '3Na^+_{out} + 2K^+_{in} + ATP \\rightarrow 3Na^+_{in} + 2K^+_{out} + ADP + P_i',
            description: '3 Na+ out, 2 K+ in per ATP',
        },
        {
            id: 'nernst-na',
            name: 'Nernst Potential (Na+)',
            latex: 'E_{Na} = \\frac{RT}{zF}\\ln\\frac{[Na^+]_{out}}{[Na^+]_{in}}',
            description: 'Equilibrium potential for sodium',
        },
        {
            id: 'nernst-k',
            name: 'Nernst Potential (K+)',
            latex: 'E_K = \\frac{RT}{zF}\\ln\\frac{[K^+]_{out}}{[K^+]_{in}}',
            description: 'Equilibrium potential for potassium',
        },
        {
            id: 'goldman-equation',
            name: 'Goldman-Hodgkin-Katz Equation',
            latex: 'V_m = \\frac{RT}{F}\\ln\\frac{P_K[K^+]_{out} + P_{Na}[Na^+]_{out}}{P_K[K^+]_{in} + P_{Na}[Na^+]_{in}}',
            description: 'Resting membrane potential',
        },
    ],
    derivatives: (_state, _params) => ({
        Na_in: 0,
        K_in: 0,
        V_m: 0,
    }),
    derivedState: (state, params) => {
        const Na_in = getState(state, 'Na_in');
        const K_in = getState(state, 'K_in');
        const V_m = getState(state, 'V_m');
        const Na_out = getParam(params, 'Na_out');
        const K_out = getParam(params, 'K_out');
        const V_max = getParam(params, 'V_max');
        const T = getParam(params, 'T');
        const F = 96485.33212; // Faraday constant C/mol
        // Nernst potentials
        const E_Na = (R * T / F) * Math.log(Na_out / Na_in) * 1000; // mV
        const E_K = (R * T / F) * Math.log(K_out / K_in) * 1000; // mV
        // GHK approximation (using typical permeability ratio P_K:P_Na ≈ 100:1)
        const P_K = 1.0;
        const P_Na = 0.04;
        const V_m_GHK = (R * T / F) * Math.log((P_K * K_out + P_Na * Na_out) / (P_K * K_in + P_Na * Na_in)) * 1000;
        // Pump activity
        const Na_flux_out = 3 * V_max;
        const K_flux_in = 2 * V_max;
        const net_charge_movement = -1;
        return {
            E_Na,
            E_K,
            V_m_calculated: V_m_GHK,
            Na_gradient: Na_out - Na_in,
            K_gradient: K_in - K_out,
            Na_flux_out,
            K_flux_in,
            electrogenic_contribution: net_charge_movement * V_max,
            driving_force_Na: E_Na - V_m,
            driving_force_K: E_K - V_m,
            ATP_consumption_rate: V_max,
            at_equilibrium: Math.abs(V_m - V_m_GHK) < 5 ? 1 : 0,
        };
    },
    failureModes: [
        {
            id: 'negative-concentration',
            name: 'Negative Concentration',
            check: (_state, _params) => {
                const Na_in = getState(getStateValues(_state), 'Na_in');
                const K_in = getState(getStateValues(_state), 'K_in');
                return Na_in < 0 || K_in < 0;
            },
            severity: 'error',
            educationalMessage: 'Ion concentrations cannot be negative.',
        },
        {
            id: 'reversed-gradients',
            name: 'Reversed Ion Gradients',
            check: (_state, params) => {
                const Na_in = getState(getStateValues(_state), 'Na_in');
                const K_in = getState(getStateValues(_state), 'K_in');
                const Na_out = getParam(params, 'Na_out');
                const K_out = getParam(params, 'K_out');
                return Na_in >= Na_out || K_in <= K_out;
            },
            severity: 'error',
            educationalMessage: 'Ion gradients are reversed. Na+ should be higher outside and K+ higher inside for normal cell physiology.',
        },
    ],
    bounds: {
        Na_in: { min: 0, max: 200 },
        K_in: { min: 0, max: 200 },
        V_m: { min: -150, max: 50 },
    },
});
// ============================================================================
// CHEMIOSMOSIS (PROTON GRADIENT)
// ============================================================================
export const Chemiosmosis = createExperimentFromSchema({
    id: 'chemiosmosis',
    name: 'Chemiosmosis (Proton Gradient)',
    domain: 'biology',
    description: 'ATP synthesis driven by proton gradient across membrane',
    conceptTags: ['Chemiosmosis', 'Proton Motive Force', 'ATP Synthase', 'Oxidative Phosphorylation'],
    difficulty: 7,
    stateVariables: [
        { id: 'pH_in', name: 'Matrix pH', unit: '', initial: 8, symbol: 'pH_{in}', description: 'pH inside mitochondrial matrix' },
        { id: 'Delta_psi', name: 'Membrane Potential', unit: 'mV', initial: 150, symbol: '\\Delta\\Psi', description: 'Electrical potential across inner membrane' },
    ],
    parameters: [
        { id: 'pH_out', name: 'Intermembrane Space pH', unit: '', min: 5, max: 7.5, default: 7, symbol: 'pH_{out}', description: 'pH in intermembrane space' },
        { id: 'H_per_ATP', name: 'Protons per ATP', unit: '', min: 3, max: 5, default: 4, symbol: 'n_H^+', description: 'Protons needed per ATP synthesized' },
        { id: 'T', name: 'Temperature', unit: 'K', min: 290, max: 315, default: 310.15, symbol: 'T' },
    ],
    equations: [
        {
            id: 'pmf',
            name: 'Proton Motive Force',
            latex: '\\Delta p = \\Delta\\Psi - 2.303\\frac{RT}{F}\\Delta pH',
            description: 'Total driving force for ATP synthesis (mV)',
        },
        {
            id: 'delta-ph-component',
            name: 'pH Component',
            latex: '\\Delta pH_{component} = 2.303\\frac{RT}{F}(pH_{out} - pH_{in})',
            description: 'Chemical component of PMF',
        },
        {
            id: 'atp-free-energy',
            name: 'Free Energy per ATP',
            latex: '\\Delta G_{ATP} = n_H^+ F \\Delta p',
            description: 'Energy available for ATP synthesis',
        },
    ],
    derivatives: (_state, _params) => ({
        pH_in: 0,
        Delta_psi: 0,
    }),
    derivedState: (state, params) => {
        const pH_in = getState(state, 'pH_in');
        const Delta_psi = getState(state, 'Delta_psi');
        const pH_out = getParam(params, 'pH_out');
        const H_per_ATP = getParam(params, 'H_per_ATP');
        const T = getParam(params, 'T');
        const F = 96485.33212; // Faraday constant
        const Delta_pH = pH_out - pH_in;
        const pH_component_mV = 2.303 * (R * T / F) * Delta_pH * 1000;
        const pmf = Delta_psi + pH_component_mV;
        const Delta_G_ATP = H_per_ATP * F * (pmf / 1000); // J/mol
        const Delta_G_ATP_kJ = Delta_G_ATP / 1000;
        const H_concentration_in = Math.pow(10, -pH_in);
        const H_concentration_out = Math.pow(10, -pH_out);
        const H_ratio = H_concentration_out / H_concentration_in;
        return {
            Delta_pH,
            pH_component_mV,
            pmf,
            Delta_G_ATP_kJ,
            H_concentration_in_mM: H_concentration_in * 1000,
            H_concentration_out_mM: H_concentration_out * 1000,
            H_ratio,
            atp_synthesis_favorable: Delta_G_ATP < -50000 ? 1 : 0,
            electrical_component_fraction: Delta_psi / pmf,
            chemical_component_fraction: pH_component_mV / pmf,
        };
    },
    failureModes: [
        {
            id: 'negative-potential',
            name: 'Negative Membrane Potential',
            check: (_state, _params) => {
                const Delta_psi = getState(getStateValues(_state), 'Delta_psi');
                return Delta_psi < 0;
            },
            severity: 'error',
            educationalMessage: 'A positive membrane potential (outside positive) is required for ATP synthesis in mitochondria.',
        },
        {
            id: 'insufficient-pmf',
            name: 'Insufficient Proton Motive Force',
            check: (_state, params) => {
                const pH_in = getState(getStateValues(_state), 'pH_in');
                const Delta_psi = getState(getStateValues(_state), 'Delta_psi');
                const pH_out = getParam(params, 'pH_out');
                const T = getParam(params, 'T');
                const F = 96485.33212;
                const Delta_pH = pH_out - pH_in;
                const pH_component_mV = 2.303 * (R * T / F) * Delta_pH * 1000;
                const pmf = Delta_psi + pH_component_mV;
                return pmf < 100;
            },
            severity: 'warning',
            educationalMessage: 'PMF is below 100 mV, which may be insufficient for efficient ATP synthesis. Typical values are 180-220 mV.',
        },
    ],
    bounds: {
        pH_in: { min: 6, max: 9 },
        Delta_psi: { min: 0, max: 250 },
    },
});
//# sourceMappingURL=cell-transport.js.map